/**
 * Cobros pendientes — `OD-0051` (ACCEPTED), la opción `D-04 B` de `OD-0040`:
 * «sección propia con su lista y sus filtros».
 *
 * Vive DENTRO de Facturas y no en el menú: se llega desde la cifra «Pendiente
 * de cobro» de la lista, que es donde uno se pregunta quién le debe (`D-01`).
 *
 * ## Lo que esta pantalla NO decide
 *
 * - **Ninguna cuenta de dinero** (`OD-0040 D-08`, `URS §129`): el total, cada
 *   tramo de antigüedad y cada pendiente llegan sumados de la API. Por eso los
 *   filtros van a la API y no se aplican aquí: filtrar en la web obligaría a
 *   sumar lo que queda.
 * - **Ni qué está vencido** (`D-03`): los días de retraso y el tramo los
 *   calcula la API con la fecha de Madrid.
 * - **Ni si el plan la incluye** (`OD-0040 D-09`): fuera de Business la API
 *   contesta 402 y aquí se explica, con el enlace a los planes.
 *
 * Cada fila emitida lleva «Apuntar seguimiento del cobro» (`OD-0050-ENMIENDA-01`
 * E-04): una tarea enlazada a esa factura. Apuntarla no toca la factura ni el
 * cobro: solo crea una actividad.
 *
 * `OD-0062` (ACCEPTED 2026-10-25) añade, sin que la lista escriba por su cuenta:
 *
 * ```text
 * E-02  en las que no tienen vencimiento, los días desde la emisión, y lo que
 *       llevan más de 30 días (sumado por la API)
 * E-04  «Apuntar cobro» abre AQUI el formulario de cobro de esa factura
 *       (TarjetaCobros, la misma ruta y las mismas reglas), y «Recordar»
 *       prepara el mensaje en el correo o el WhatsApp del usuario
 * E-05  «Descargar Excel» con lo filtrado. Lo genera la API
 * ```
 */
import { Fragment, useCallback, useEffect, useState } from "react";

import { api, FILTROS_PENDIENTES, type CobrosPendientes, type FiltroPendientes, type FiltrosPendientes } from "../api/cliente";
import { t } from "../i18n/es";
import { importeLegible } from "../ui/formato";
import { Icono } from "../ui/Iconos";
import { Aviso, Boton, Campo, FilaPulsable, Selector, Tarjeta } from "../ui/primitivas";
import { FormularioActividad, seguimientoDeCobro } from "./Actividades";
import { diaLegible } from "./PantallaOportunidades";
import { RecordarCobro } from "./RecordarCobro";
import { TarjetaCobros } from "./TarjetaCobros";

type Estado =
  | { readonly tipo: "cargando" }
  | { readonly tipo: "error" }
  | { readonly tipo: "sin-permiso" }
  | { readonly tipo: "solo-business" }
  | { readonly tipo: "filtros"; readonly campos: Readonly<Record<string, string>> }
  | { readonly tipo: "datos"; readonly datos: CobrosPendientes };

const SIN_FILTROS: FiltrosPendientes = { estado: "PENDING", cliente: null, desde: null, hasta: null };
const TODOS = "__todos__";

/** Entrega el fichero al navegador como una descarga normal, con su nombre. */
function guardar(contenido: Blob, nombre: string): void {
  const url = URL.createObjectURL(contenido);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Lo que hay abierto debajo de una fila: como mucho una cosa a la vez. */
type Abierta = { readonly id: string; readonly que: "cobro" | "recordar" | "seguimiento" };

export function PantallaCobrosPendientes({
  filtroInicial = "PENDING",
  hayConexion,
  onAbrir,
  onVolver,
  onVerPlan,
  onSesionCaducada,
}: {
  /** `OD-0062` E-11: desde la campana se llega con «Vencidas» elegido. */
  filtroInicial?: FiltroPendientes;
  hayConexion: boolean;
  onAbrir: (facturaId: string) => void;
  onVolver: () => void;
  onVerPlan: () => void;
  onSesionCaducada: () => void;
}) {
  const [filtros, setFiltros] = useState<FiltrosPendientes>({ ...SIN_FILTROS, estado: filtroInicial });
  const [estado, setEstado] = useState<Estado>({ tipo: "cargando" });
  // El desplegable de clientes no se vacía mientras se recarga.
  const [ultimo, setUltimo] = useState<CobrosPendientes | null>(null);
  // La fila con algo abierto debajo, y las que ya tienen su seguimiento.
  const [abierta, setAbierta] = useState<Abierta | null>(null);
  const [apuntadas, setApuntadas] = useState<ReadonlySet<string>>(new Set());
  const [descargando, setDescargando] = useState(false);
  const [errorDescarga, setErrorDescarga] = useState(false);

  // `silencioso`: tras apuntar un cobro desde una fila, la lista se pone al día
  // sin vaciarse, y el formulario abierto sigue en su sitio.
  const cargar = useCallback(async (silencioso = false): Promise<void> => {
    if (!silencioso) setEstado({ tipo: "cargando" });
    const r = await api.cobrosPendientes(filtros);
    if (!r.ok) {
      if (r.estado === 401) onSesionCaducada();
      else if (r.estado === 402) setEstado({ tipo: "solo-business" });
      else if (r.estado === 403) setEstado({ tipo: "sin-permiso" });
      else if (r.estado === 422 && r.error !== null) setEstado({ tipo: "filtros", campos: r.error.campos });
      else setEstado({ tipo: "error" });
      return;
    }
    setUltimo(r.datos);
    setEstado({ tipo: "datos", datos: r.datos });
  }, [filtros, onSesionCaducada]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // E-05: lo filtrado, generado por la API. Un 402 o un 403 ya los explica la lista.
  async function descargar(): Promise<void> {
    setDescargando(true);
    setErrorDescarga(false);
    const r = await api.descargarPendientes(filtros);
    setDescargando(false);
    if (r.ok) return guardar(r.datos.contenido, r.datos.nombre);
    if (r.estado === 401) return onSesionCaducada();
    setErrorDescarga(true);
  }

  const cabecera = (
    <div className="barra-acciones">
      <Boton variante="secundario" onClick={onVolver}>
        {t.pendientes.volver}
      </Boton>
      {estado.tipo === "datos" && estado.datos.filas.length > 0 ? (
        <Boton variante="secundario" onClick={() => void descargar()} deshabilitado={descargando || !hayConexion}>
          {descargando ? t.pendientes.descargando : t.pendientes.descargar}
        </Boton>
      ) : null}
    </div>
  );

  if (estado.tipo === "solo-business") {
    return (
      <Tarjeta titulo={t.pendientes.titulo}>
        {cabecera}
        <div className="estado-vacio">
          <span className="estado-vacio-icono" aria-hidden="true">
            <Icono nombre="facturas" />
          </span>
          <p className="estado-vacio-titulo">{t.pendientes.soloBusinessTitulo}</p>
          <p>{t.pendientes.soloBusiness}</p>
          <Boton onClick={onVerPlan}>{t.pendientes.verPlanes}</Boton>
        </div>
      </Tarjeta>
    );
  }

  const datos = estado.tipo === "datos" ? estado.datos : null;
  const clientes = (datos ?? ultimo)?.clientes ?? [];
  const campos = estado.tipo === "filtros" ? estado.campos : {};
  const hayFiltros = filtros.estado !== "PENDING" || filtros.cliente !== null || filtros.desde !== null || filtros.hasta !== null;
  const errorDe = (campo: string): string | null => {
    const c = campos[campo];
    return c === undefined ? null : (t.pendientes.errores[c] ?? null);
  };

  return (
    <Tarjeta titulo={t.pendientes.titulo}>
      {cabecera}
      {errorDescarga ? <Aviso tono="error">{hayConexion ? t.pendientes.errorDescarga : t.comun.sinRed}</Aviso> : null}

      {datos !== null ? (
        <p className="ayuda-neutra">
          {t.pendientes.aFecha(diaLegible(datos.aFecha))} · {t.pendientes.total(importeLegible(datos.pendiente), datos.facturas)}
        </p>
      ) : null}

      <div className="fichas" role="group" aria-label={t.pendientes.filtroEstado}>
        {FILTROS_PENDIENTES.map((f: FiltroPendientes) => (
          <button
            key={f}
            type="button"
            className="ficha"
            aria-pressed={filtros.estado === f}
            onClick={() => setFiltros({ ...filtros, estado: f })}
          >
            <span>{t.pendientes.estados[f]}</span>
          </button>
        ))}
      </div>

      <div className="pendientes-filtros">
        <Selector
          id="pendientes-cliente"
          etiqueta={t.pendientes.cliente}
          valor={filtros.cliente ?? TODOS}
          opciones={[{ valor: TODOS, texto: t.pendientes.todosLosClientes }, ...clientes.map((c) => ({ valor: c.customerId, texto: c.displayName }))]}
          onChange={(v) => setFiltros({ ...filtros, cliente: v === TODOS ? null : v })}
        />
        <Campo
          id="pendientes-desde"
          etiqueta={t.pendientes.desde}
          tipo="date"
          requerido={false}
          valor={filtros.desde ?? ""}
          error={errorDe("desde")}
          onChange={(v) => setFiltros({ ...filtros, desde: v === "" ? null : v })}
        />
        <Campo
          id="pendientes-hasta"
          etiqueta={t.pendientes.hasta}
          tipo="date"
          requerido={false}
          valor={filtros.hasta ?? ""}
          error={errorDe("hasta")}
          onChange={(v) => setFiltros({ ...filtros, hasta: v === "" ? null : v })}
        />
        {hayFiltros ? (
          <Boton variante="secundario" onClick={() => setFiltros(SIN_FILTROS)}>
            {t.pendientes.limpiar}
          </Boton>
        ) : null}
      </div>

      {/* D-03: los seis tramos, sumados por la API. Siempre los seis, para que
          un tramo a cero se vea a cero y no desaparezca. */}
      {datos !== null ? (
        <section aria-label={t.pendientes.antiguedad}>
          <h3 className="subtitulo">{t.pendientes.antiguedad}</h3>
          <ul className="pendientes-tramos">
            {datos.tramos.map((tr) => (
              <li
                key={tr.bucket}
                className={`pendientes-tramo pendientes-tramo-${tr.bucket}${tr.invoices === 0 ? " pendientes-tramo-vacio" : ""}`}
              >
                <span className="pendientes-tramo-etiqueta">{t.pendientes.tramos[tr.bucket]}</span>
                <span className="pendientes-tramo-importe">{importeLegible(tr.pending)} €</span>
                <span className="pendientes-tramo-cuantas">{t.pendientes.tramoFacturas(tr.invoices)}</span>
              </li>
            ))}
          </ul>
          {/* E-02: no cuentan como vencidas (D-03), pero se dice cuánto llevan. */}
          {datos.sinVencimientoAntiguas !== null && datos.sinVencimientoAntiguas.facturas > 0 ? (
            <Aviso tono="aviso">
              {t.pendientes.sinVencimientoAntiguas(
                importeLegible(datos.sinVencimientoAntiguas.pendiente),
                datos.sinVencimientoAntiguas.facturas,
                datos.sinVencimientoAntiguas.dias,
              )}
            </Aviso>
          ) : null}
        </section>
      ) : null}

      {estado.tipo === "cargando" ? <p>{t.pendientes.cargando}</p> : null}
      {estado.tipo === "sin-permiso" ? <Aviso tono="aviso">{t.pendientes.sinPermiso}</Aviso> : null}
      {estado.tipo === "error" ? (
        <>
          <Aviso tono="error">{t.pendientes.error}</Aviso>
          <Boton variante="secundario" onClick={() => void cargar()}>
            {t.pendientes.reintentar}
          </Boton>
        </>
      ) : null}

      {datos !== null && datos.filas.length === 0 ? <Aviso tono="info">{hayFiltros ? t.pendientes.vacioFiltro : t.pendientes.vacio}</Aviso> : null}
      {datos !== null && datos.filas.length > 0 ? (
        <ul className="lista-filas">
          {datos.filas.map((f) => (
            <Fragment key={f.invoiceId}>
              <FilaPulsable onClick={() => onAbrir(f.invoiceId)}>
                <span className="fila-titulo">
                  {f.number ?? t.pendientes.sinNumero} · {f.customerName}
                </span>
                <span className="ayuda-neutra">
                  <span className={`insignia insignia-pago-${f.paymentStatus}`}>{t.cobros.estados[f.paymentStatus]}</span>
                  {" · "}
                  {t.pendientes.deTotal(importeLegible(f.pending), importeLegible(f.amountToCollect))}
                </span>
                <span className="ayuda-neutra">
                  {f.issueDate !== null ? `${t.pendientes.emitida(diaLegible(f.issueDate))} · ` : ""}
                  {f.dueDate !== null ? t.pendientes.vence(diaLegible(f.dueDate)) : t.pendientes.sinVencimiento}
                  {f.dueDate === null && f.daysSinceIssue !== null ? ` · ${t.pendientes.desdeEmision(f.daysSinceIssue)}` : ""}
                  {f.daysOverdue !== null && f.daysOverdue > 0 ? (
                    <>
                      {" · "}
                      <strong className="pendientes-retraso">{t.pendientes.retraso(f.daysOverdue)}</strong>
                    </>
                  ) : null}
                </span>
              </FilaPulsable>
              {f.number !== null ? (
                <li className="pendientes-seguimiento">
                  <div className="fila-botones">
                    <button type="button" className="enlace-en-texto" aria-expanded={abierta?.id === f.invoiceId && abierta.que === "cobro"} onClick={() => setAbierta({ id: f.invoiceId, que: "cobro" })}>
                      {t.pendientes.apuntarCobro}
                    </button>
                    <button type="button" className="enlace-en-texto" aria-expanded={abierta?.id === f.invoiceId && abierta.que === "recordar"} onClick={() => setAbierta({ id: f.invoiceId, que: "recordar" })}>
                      {t.pendientes.recordarBoton}
                    </button>
                    {apuntadas.has(f.invoiceId) ? (
                      <span className="ayuda-neutra">{t.tareas.seguimientoApuntado}</span>
                    ) : (
                      <button type="button" className="enlace-en-texto" onClick={() => setAbierta({ id: f.invoiceId, que: "seguimiento" })}>
                        {t.tareas.seguimientoCobro}
                      </button>
                    )}
                  </div>
                  {abierta?.id === f.invoiceId && abierta.que === "seguimiento" ? (
                    <FormularioActividad
                      facturaFija={f.invoiceId}
                      plantilla={seguimientoDeCobro(f.number).plantilla}
                      onGuardada={() => {
                        setAbierta(null);
                        setApuntadas(new Set(apuntadas).add(f.invoiceId));
                      }}
                      onCancelar={() => setAbierta(null)}
                      onSesionCaducada={onSesionCaducada}
                    />
                  ) : null}
                  {abierta?.id === f.invoiceId && abierta.que === "cobro" ? (
                    <>
                      {/* La misma tarjeta que la ficha de la factura: su ruta, su permiso y sus reglas. */}
                      <TarjetaCobros
                        facturaId={f.invoiceId}
                        hayConexion={hayConexion}
                        onCuenta={(c) => {
                          if (c.trasApuntar) void cargar(true);
                        }}
                        onSesionCaducada={onSesionCaducada}
                      />
                      <Boton variante="terciario" onClick={() => setAbierta(null)}>
                        {t.pendientes.cerrarCobro}
                      </Boton>
                    </>
                  ) : null}
                  {abierta?.id === f.invoiceId && abierta.que === "recordar" ? (
                    <RecordarCobro factura={f} onCerrar={() => setAbierta(null)} onSesionCaducada={onSesionCaducada} />
                  ) : null}
                </li>
              ) : null}
            </Fragment>
          ))}
        </ul>
      ) : null}

      {datos !== null && datos.tramos.some((tr) => tr.bucket === "NO_DUE_DATE" && tr.invoices > 0) ? (
        <p className="ayuda-neutra">{t.pendientes.ayudaSinVencimiento}</p>
      ) : null}
    </Tarjeta>
  );
}
